import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, FormGroup,
  Grid, IconButton, Paper, Radio, RadioGroup, Step, StepLabel, Stepper, TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LayersIcon from '@mui/icons-material/Layers';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, sectionCardSx, insetPanelSx, sectionHeadingSx, statusChipSx, actionRowSx,
} from '../portalStyles';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading } from '../../../components/portal/PortalStates';
import LazyOrgTreeCheckbox from '../../../components/portal/LazyOrgTreeCheckbox';
import MultiSelectField from '../securityhub/MultiSelectField';
import { AWS_REGIONS } from '../../../constants/awsRegions';
import { createStackSet, getStackSetsOrgChildren, validateTemplate } from '../../../services/stacksetsApi';
import { useLazyOrgTree } from '../../../hooks/useLazyOrgTree';
import { countAccountsUnderOUs } from '../../../utils/orgCount';

const STEPS = ['Name', 'Template', 'Targets', 'Options', 'Review'];
const MAX_TEMPLATE_BYTES = 460800;

// One measure for every wizard step body so the form does not re-flow its
// width as the user advances (shared with StackSetEdit / StackSetsSetup).
const STEP_MAX_WIDTH = 720;

// Admin-console stepper: Metropolis labels, accent for the active step.
const stepperSx = {
  mb: 3,
  overflowX: 'auto',
  '& .MuiStepLabel-label': { fontFamily: FONT_BODY, fontSize: '0.875rem', fontWeight: 500, color: CK.body },
  '& .MuiStepLabel-label.Mui-active': { fontWeight: 600, color: CK.navy },
  '& .MuiStepLabel-label.Mui-completed': { fontWeight: 600, color: CK.navy },
  '& .MuiStepIcon-root.Mui-active': { color: CK.blue },
  '& .MuiStepIcon-root.Mui-completed': { color: CK.green },
};

export default function StackSetCreate() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [templateSource, setTemplateSource] = useState('upload'); // 'upload' | 's3url'
  const [templateBody, setTemplateBody] = useState('');
  const [templateS3Url, setTemplateS3Url] = useState('');
  const [validating, setValidating] = useState(false);
  const [summary, setSummary] = useState(null); // {description, capabilities, parameters}
  const [paramValues, setParamValues] = useState({});
  const [capsAccepted, setCapsAccepted] = useState(false);
  const tree = useLazyOrgTree(getStackSetsOrgChildren);
  // {status, accounts}: 'idle' before a selection, 'counting' while the selected
  // subtrees are walked, 'done' with the exact ACTIVE accounts, 'error' when a
  // level failed — never a low number presented as fact.
  const [count, setCount] = useState({ status: 'idle', accounts: [] });
  const [selectedOUs, setSelectedOUs] = useState([]); // [{id,name,type}]
  const [selectedAccounts, setSelectedAccounts] = useState([]); // [{id,name,type}]
  const [regions, setRegions] = useState([]);
  const [failureTolerance, setFailureTolerance] = useState(10);
  const [maxConcurrent, setMaxConcurrent] = useState(100);
  const [tags, setTags] = useState([]); // [{key, value}] rows
  const [submitting, setSubmitting] = useState(false);

  // Counting walks ONLY the selected subtrees, sharing the picker's cache. A
  // generation counter discards a walk whose selection is already stale.
  const countGenRef = useRef(0);
  const runCount = useCallback(async () => {
    const gen = ++countGenRef.current;
    if (selectedOUs.length === 0 && selectedAccounts.length === 0) {
      setCount({ status: 'idle', accounts: [] });
      return;
    }
    setCount({ status: 'counting', accounts: [] });
    try {
      const res = await countAccountsUnderOUs(selectedOUs.map((o) => o.id), {
        getChildren: tree.loadChildren,
        cache: tree.cacheRef.current,
        isCancelled: () => countGenRef.current !== gen,
      });
      if (res.cancelled || countGenRef.current !== gen) return;
      // Explicit accounts cost no AWS calls — countAccountsUnderOUs with an empty
      // list issues no requests — so an account-only selection counts instantly.
      const union = [...new Set([...res.accounts, ...selectedAccounts.map((a) => a.id)])];
      setCount({ status: 'done', accounts: union });
    } catch (e) {
      if (countGenRef.current !== gen) return;
      // Carry the reason: loadChildren records it on tree.errors[nodeId], which
      // only renders on that node's row — and a counting walk fetches levels the
      // user never expanded, so this is the only place the user can see it.
      setCount({ status: 'error', accounts: [], message: e.message });
    }
  }, [selectedOUs, selectedAccounts, tree.loadChildren, tree.cacheRef]);

  useEffect(() => {
    runCount();
    // Abandon an in-flight walk when the wizard goes away: nothing else advances
    // the generation once this component is gone, and a serial walk left running
    // keeps spending the tenant's Organizations throughput for nothing.
    return () => { countGenRef.current += 1; };
  }, [runCount]);

  // A re-read hierarchy invalidates any count computed from the old one, and
  // any walk still in flight against it.
  const refreshHierarchy = useCallback(() => {
    countGenRef.current += 1;
    setCount({ status: 'idle', accounts: [] });
    tree.refresh();
    if (selectedOUs.length > 0 || selectedAccounts.length > 0) runCount();
  }, [tree, selectedOUs, selectedAccounts, runCount]);

  const previewAccounts = count.accounts;

  // Review's selection chip, built from the non-empty halves only. "2 OU(s), 0
  // account(s)" sat next to the resolved blast-radius chip, putting two different
  // account numbers on one row of the one screen that has to be unambiguous.
  // Step 3's Next gate requires at least one OU or account, so this is never ''.
  const selectionLabel = [
    selectedOUs.length > 0 ? `${selectedOUs.length} OU(s)` : null,
    selectedAccounts.length > 0 ? `${selectedAccounts.length} account(s)` : null,
  ].filter(Boolean).join(', ');

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_TEMPLATE_BYTES) {
      setError(`Template exceeds the ${Math.floor(MAX_TEMPLATE_BYTES / 1024)}KB limit`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { setTemplateBody(String(reader.result)); setSummary(null); setError(null); };
    reader.onerror = () => setError('Could not read the selected file');
    reader.readAsText(file);
    // Reset the input so re-selecting the same file fires onChange again
    // (readAsText has already captured the file; clearing is safe here).
    e.target.value = '';
  };

  const runValidate = async () => {
    setValidating(true);
    setError(null);
    try {
      const isURL = templateSource === 's3url';
      const res = await validateTemplate(isURL ? '' : templateBody, isURL ? templateS3Url.trim() : '');
      const s = res?.data || {};
      setSummary(s);
      const defaults = {};
      (s.parameters || []).forEach((p) => { defaults[p.key] = p.defaultValue || ''; });
      setParamValues(defaults);
      setCapsAccepted(!(s.capabilities || []).length);
      setActiveStep(2);
    } catch (e) {
      setError(e.message);
    } finally {
      setValidating(false);
    }
  };

  const setTagField = (idx, field, val) => {
    setTags((cur) => cur.map((t, i) => (i === idx ? { ...t, [field]: val } : t)));
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await createStackSet({
        name: name.trim(),
        description,
        templateBody: templateSource === 'upload' ? templateBody : '',
        templateS3Url: templateSource === 's3url' ? templateS3Url.trim() : '',
        // noEcho travels with each parameter (from the validation summary) so
        // the Detail page can mask secret values.
        parameters: Object.entries(paramValues).map(([key, value]) => ({ key, value, noEcho: !!(summary?.parameters || []).find((p) => p.key === key)?.noEcho })),
        capabilities: summary?.capabilities || [],
        tags: Object.fromEntries(tags.filter((t) => t.key).map((t) => [t.key, t.value])),
        targetOus: selectedOUs.map((o) => ({ id: o.id, name: o.name })),
        targetAccounts: selectedAccounts.map((a) => ({ id: a.id, name: a.name })),
        regions,
        operationPrefs: {
          failureTolerancePercentage: Number(failureTolerance),
          maxConcurrentPercentage: Number(maxConcurrent),
          regionConcurrencyType: 'PARALLEL',
        },
      });
      navigate(`/stacksets/${res?.data?.id}`);
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
        <PortalPageHeader icon={<LayersIcon sx={{ fontSize: 24 }} />} title="Create StackSet" subtitle="Deploy a CloudFormation template across your OUs and accounts" />
      </motion.div>
      {error && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>{error}</Alert>
        </motion.div>
      )}
      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={sectionCardSx}>
          <Stepper activeStep={activeStep} sx={stepperSx}>
            {STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
          </Stepper>

          {activeStep === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: STEP_MAX_WIDTH }}>
              <TextField
                label="StackSet name" value={name} onChange={(e) => setName(e.target.value)}
                helperText="Starts with a letter; letters, digits and hyphens only; max 128 characters"
              />
              <TextField label="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} multiline rows={2} />
              <Box sx={actionRowSx}>
                <Button variant="contained" disabled={!/^[a-zA-Z][a-zA-Z0-9-]{0,127}$/.test(name.trim())} onClick={() => setActiveStep(1)}>Next</Button>
              </Box>
            </Box>
          )}

          {activeStep === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: STEP_MAX_WIDTH }}>
              <Box>
                <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Template source</Typography>
                <RadioGroup
                  row value={templateSource}
                  onChange={(e) => { setTemplateSource(e.target.value); setSummary(null); }}
                >
                  <FormControlLabel value="upload" control={<Radio />} label="Upload a template file" />
                  <FormControlLabel value="s3url" control={<Radio />} label="Amazon S3 URL" />
                </RadioGroup>
              </Box>

              {templateSource === 'upload' ? (
                <>
                  <Button variant="outlined" component="label" sx={{ alignSelf: 'flex-start' }}>
                    Upload template file (.json / .yaml)
                    <input hidden type="file" accept=".json,.yaml,.yml,.template" onChange={onFile} />
                  </Button>
                  <TextField
                    label="Template body" value={templateBody}
                    onChange={(e) => { setTemplateBody(e.target.value); setSummary(null); }}
                    multiline rows={14} sx={{ fontFamily: 'monospace' }}
                  />
                </>
              ) : (
                <>
                  <TextField
                    label="Amazon S3 URL" value={templateS3Url} placeholder="https://my-bucket.s3.us-east-1.amazonaws.com/templates/app.yaml"
                    onChange={(e) => { setTemplateS3Url(e.target.value); setSummary(null); }}
                    helperText="s3:// or https://…s3…amazonaws.com URL to a template in an account the StackSet admin role can read. The URL is stored and re-read on each update — update the file in place to roll out changes."
                  />
                  <Alert severity="info" sx={{ borderRadius: '8px' }}>
                    CloudFormation reads this URL directly. Keep the object in place: updating it and
                    redeploying pulls the new template — no re-upload needed.
                  </Alert>
                </>
              )}

              <Box sx={actionRowSx}>
                <Button onClick={() => setActiveStep(0)}>Back</Button>
                <Button
                  variant="contained"
                  disabled={validating || (templateSource === 'upload' ? !templateBody.trim() : !templateS3Url.trim())}
                  onClick={runValidate}
                >
                  {validating ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : 'Validate & continue'}
                </Button>
              </Box>
            </Box>
          )}

          {activeStep === 2 && (
            <Box sx={{ maxWidth: STEP_MAX_WIDTH }}>
              {(summary?.parameters || []).length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Template parameters</Typography>
                  <Grid container spacing={2}>
                    {(summary.parameters || []).map((p) => (
                      <Grid key={p.key} size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          label={p.key} helperText={p.description} type={p.noEcho ? 'password' : 'text'}
                          value={paramValues[p.key] ?? ''} onChange={(e) => setParamValues({ ...paramValues, [p.key]: e.target.value })}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Targets</Typography>
              {tree.topError ? (
                <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }} action={
                  <Button color="inherit" size="small" onClick={refreshHierarchy}>Try again</Button>
                }>
                  Could not load the organization hierarchy: {tree.topError}
                </Alert>
              ) : !tree.root ? (
                <PortalLoading label="Loading the organization hierarchy…" py={4} />
              ) : (
                <Paper elevation={0} sx={{ ...insetPanelSx, p: 1, maxHeight: 320, overflow: 'auto', mb: 2 }}>
                  <LazyOrgTreeCheckbox
                    node={tree.root}
                    tree={tree}
                    onToggleSelect={(sel) => {
                      // Routed by kind against an explicit list, never by falling
                      // through to a default: an ACCOUNT must not reach selectedOUs,
                      // which flows straight into targetOus where the backend expects
                      // OU ids only. An `else` would make any node kind the API grows
                      // an OU target silently, so an unrecognised kind is dropped —
                      // a target the user cannot see beats a bad id sent to AWS.
                      // ORGANIZATIONAL_UNIT is the API's other spelling of OU, and a
                      // ROOT id is accepted wherever an OU id is.
                      const toggle = (cur) => (cur.some((c) => c.id === sel.id)
                        ? cur.filter((c) => c.id !== sel.id) : [...cur, sel]);
                      if (sel.type === 'ACCOUNT') {
                        setSelectedAccounts(toggle);
                      } else if (sel.type === 'OU' || sel.type === 'ORGANIZATIONAL_UNIT' || sel.type === 'ROOT') {
                        setSelectedOUs(toggle);
                      }
                    }}
                    // Both lists, or a ticked account row would not render ticked.
                    selectedIds={[...selectedOUs, ...selectedAccounts]}
                  />
                </Paper>
              )}
              <Alert severity={count.status === 'error' ? 'warning' : 'info'} sx={{ mb: 2, borderRadius: '8px' }}>
                {count.status === 'idle' && 'Select one or more OUs or accounts to see how many accounts will receive stacks.'}
                {count.status === 'counting' && 'Counting accounts…'}
                {count.status === 'error' && (
                  <>Account count unavailable{count.message ? `: ${count.message}` : ''} — a level of the hierarchy failed to load. <Button size="small" onClick={runCount} sx={{ textTransform: 'none' }}>Retry</Button></>
                )}
                {count.status === 'done' && (
                  <>
                    {previewAccounts.length} account{previewAccounts.length === 1 ? '' : 's'} will receive stacks
                    {regions.length > 0 ? ` in ${regions.length} region${regions.length === 1 ? '' : 's'} (${previewAccounts.length * regions.length} stack instances)` : ''}.
                  </>
                )}
              </Alert>
              <MultiSelectField
                label="Regions" options={AWS_REGIONS} value={regions} onChange={setRegions}
              />
              <Box sx={{ ...actionRowSx, mt: 2 }}>
                <Button onClick={() => setActiveStep(1)}>Back</Button>
                <Button variant="contained" disabled={(selectedOUs.length === 0 && selectedAccounts.length === 0) || regions.length === 0} onClick={() => setActiveStep(3)}>Next</Button>
              </Box>
            </Box>
          )}

          {activeStep === 3 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: STEP_MAX_WIDTH }}>
              <TextField
                label="Failure tolerance (%)" type="number" value={failureTolerance}
                onChange={(e) => setFailureTolerance(e.target.value)}
                helperText="Percentage of accounts per region that can fail before the operation stops"
              />
              <TextField
                label="Max concurrent accounts (%)" type="number" value={maxConcurrent}
                onChange={(e) => setMaxConcurrent(e.target.value)}
              />
              <Box>
                <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Tags (optional)</Typography>
                {tags.map((t, idx) => (
                  // Rows are index-keyed on purpose: rows have no identity beyond
                  // their position, and key edits must not remount the TextField.
                  // eslint-disable-next-line react/no-array-index-key
                  <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                      label="Key" size="small" value={t.key}
                      onChange={(e) => setTagField(idx, 'key', e.target.value)}
                    />
                    <TextField
                      label="Value" size="small" value={t.value}
                      onChange={(e) => setTagField(idx, 'value', e.target.value)}
                    />
                    <IconButton size="small" aria-label="Remove tag" onClick={() => setTags((cur) => cur.filter((_, i) => i !== idx))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button size="small" startIcon={<AddIcon />} onClick={() => setTags((cur) => [...cur, { key: '', value: '' }])}>
                  Add tag
                </Button>
              </Box>
              {(summary?.capabilities || []).length > 0 && (
                <FormGroup>
                  <FormControlLabel
                    control={<Checkbox checked={capsAccepted} onChange={(e) => setCapsAccepted(e.target.checked)} />}
                    label={`I acknowledge that this template requires: ${(summary.capabilities || []).join(', ')}`}
                  />
                </FormGroup>
              )}
              <Box sx={actionRowSx}>
                <Button onClick={() => setActiveStep(2)}>Back</Button>
                <Button variant="contained" disabled={!capsAccepted && (summary?.capabilities || []).length > 0} onClick={() => setActiveStep(4)}>Next</Button>
              </Box>
            </Box>
          )}

          {activeStep === 4 && (
            <Box sx={{ maxWidth: STEP_MAX_WIDTH }}>
              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Review</Typography>
              <Box sx={{ ...insetPanelSx, display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Chip size="small" label={`Name: ${name.trim()}`} sx={statusChipSx('neutral')} />
                <Chip size="small" label={selectionLabel} sx={statusChipSx('neutral')} />
                {/* Gated on the count status: previewAccounts is [] while counting
                    and on failure, and a confident "0 accounts" on the
                    confirmation screen would read as fact. The label spells out
                    what the number MEANS — next to the selection chip, a bare
                    "12 account(s)" was indistinguishable from what was picked. */}
                <Chip
                  size="small"
                  label={count.status === 'done'
                    ? `${previewAccounts.length} account${previewAccounts.length === 1 ? '' : 's'} will receive stacks`
                    : count.status === 'counting' ? 'Counting accounts…' : 'Account count unavailable'}
                  sx={statusChipSx(count.status === 'done' ? 'neutral' : count.status === 'counting' ? 'info' : 'warning')}
                />
                <Chip size="small" label={`${regions.length} region(s)`} sx={statusChipSx('neutral')} />
                {count.status === 'done' && (
                  <Chip size="small" label={`${previewAccounts.length * regions.length} stack instances`} sx={statusChipSx('info')} />
                )}
              </Box>
              <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
                CloudFormation will create the template's resources in every targeted account and region.
              </Alert>
              <Box sx={actionRowSx}>
                <Button onClick={() => setActiveStep(3)}>Back</Button>
                <Button variant="contained" disabled={submitting} onClick={submit}>
                  {submitting ? 'Creating…' : 'Create StackSet'}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </motion.div>
    </motion.div>
  );
}
